import React from 'react';

// Minimal markdown renderer for AI-generated summaries.
//
// Supports: #/##/### headings, - and * bullet lists, **bold**, *italic*,
// `inline code`, and paragraph breaks on blank lines. That's enough for
// the executive summary + full report output described in the MI spec.
//
// We intentionally avoid pulling in react-markdown for this slice — it
// adds ~60kb of deps and we only need a handful of features. If the
// reports start using tables, links, or code blocks, swap this out.

function renderInline(text, keyPrefix) {
  const nodes = [];
  let remaining = text;
  let i = 0;
  const push = node => {
    nodes.push(typeof node === 'string' ? node : /*#__PURE__*/React.cloneElement(node, {
      key: `${keyPrefix}-${i++}`
    }));
  };

  // Greedy regex alternation: bold, italic, code. Run them in sequence.
  const pattern = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g;
  let lastIndex = 0;
  let match;
  while ((match = pattern.exec(remaining)) !== null) {
    if (match.index > lastIndex) push(remaining.slice(lastIndex, match.index));
    const token = match[0];
    if (token.startsWith('**')) {
      push(/*#__PURE__*/React.createElement("strong", null, token.slice(2, -2)));
    } else if (token.startsWith('*')) {
      push(/*#__PURE__*/React.createElement("em", null, token.slice(1, -1)));
    } else if (token.startsWith('`')) {
      push(/*#__PURE__*/React.createElement("code", null, token.slice(1, -1)));
    }
    lastIndex = match.index + token.length;
  }
  if (lastIndex < remaining.length) push(remaining.slice(lastIndex));
  return nodes;
}
function renderBlocks(source) {
  const lines = source.replace(/\r\n/g, '\n').split('\n');
  const blocks = [];
  let i = 0;
  let key = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) {
      i++;
      continue;
    }

    // Heading
    const h = /^(#{1,6})\s+(.*)$/.exec(line);
    if (h) {
      const level = h[1].length;
      const Tag = `h${Math.min(level + 2, 6)}`; // start at h3 so the page h1 wins
      blocks.push(/*#__PURE__*/React.createElement(Tag, {
        key: `k${key++}`
      }, renderInline(h[2], `k${key}`)));
      i++;
      continue;
    }

    // Unordered list
    if (/^\s*[-*]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
        const text = lines[i].replace(/^\s*[-*]\s+/, '');
        items.push(/*#__PURE__*/React.createElement("li", {
          key: `li${key++}`
        }, renderInline(text, `li${key}`)));
        i++;
      }
      blocks.push(/*#__PURE__*/React.createElement("ul", {
        key: `k${key++}`
      }, items));
      continue;
    }

    // Paragraph: join consecutive non-blank, non-list, non-heading lines.
    const para = [];
    while (i < lines.length && lines[i].trim() && !/^(#{1,6})\s+/.test(lines[i]) && !/^\s*[-*]\s+/.test(lines[i])) {
      para.push(lines[i]);
      i++;
    }
    blocks.push(/*#__PURE__*/React.createElement("p", {
      key: `k${key++}`
    }, renderInline(para.join(' '), `k${key}`)));
  }
  return blocks;
}
export default function Markdown({
  source,
  className
}) {
  if (!source) return null;
  return /*#__PURE__*/React.createElement("div", {
    className: className
  }, renderBlocks(String(source)));
}